import { useState, useEffect } from 'react';
import { Picker, CascadePicker } from 'antd-mobile';
import { getAreaList, getOrganizationList } from './api';
import './style/filterBarStyle.scss';

/**
 * 筛选栏组件
 * @param {boolean} visible - 是否显示筛选栏
 * @param {function} onFilterChange - 筛选条件变化回调
 * @param {boolean} showCategory - 是否显示类别筛选
 * @param {boolean} showArea - 是否显示地区筛选
 * @param {boolean} showOrganization - 是否显示猪场筛选
 * @param {boolean} showSort - 是否显示排序筛选
 * @param {array} defaultCategory - 默认类别值
 */
export default function FilterBar({
    visible = true,
    onFilterChange,
    showCategory = true,
    showArea = true,
    showOrganization = true,
    showSort = false,
    defaultCategory = []
}) {
    const [selectedCategory, setSelectedCategory] = useState(defaultCategory);
    const [selectedArea, setSelectedArea] = useState([]);
    const [selectedOrganization, setSelectedOrganization] = useState([]);
    const [selectedSort, setSelectedSort] = useState([]);

    const [areaOptions, setAreaOptions] = useState([]);
    const [organizationOptions, setOrganizationOptions] = useState([]);

    // 控制 Picker 显示状态
    const [categoryPickerVisible, setCategoryPickerVisible] = useState(false);
    const [areaPickerVisible, setAreaPickerVisible] = useState(false);
    const [organizationPickerVisible, setOrganizationPickerVisible] = useState(false);
    const [sortPickerVisible, setSortPickerVisible] = useState(false);

    // 类别选项
    const categoryOptions = [
        { label: '全部', value: '全部' },
        { label: '生猪', value: '生猪交易' },
        { label: '设备', value: '设备交易' },
        { label: '猪场租赁', value: '猪场租赁' },
        { label: '运输车辆', value: '运输车辆' }
    ];

    // 排序选项
    const sortOptions = [
        { label: '最新发布', value: '最新发布' },
        { label: '价格低到高', value: '价格低到高' },
        { label: '价格高到低', value: '价格高到低' }
    ];

    // 当 defaultCategory 改变时更新 selectedCategory
    useEffect(() => {
        if (defaultCategory && defaultCategory.length > 0 && defaultCategory[0] !== selectedCategory[0]) {
            setSelectedCategory(defaultCategory);
        }
    }, [JSON.stringify(defaultCategory)]);

    // 加载地区数据
    useEffect(() => {
        const loadAreaData = async () => {
            try {
                const response = await getAreaList();
                if (response && response.ok && response.data) {
                    // 将 value 转换为 label（字符串）
                    const convertAreaData = (items) => {
                        return items.map(item => ({
                            label: item.label,
                            value: item.label, // 使用 label 作为 value
                            children: item.children ? convertAreaData(item.children) : undefined
                        }));
                    };

                    const options = [
                        { label: '全部', value: '全部' },
                        ...convertAreaData(response.data)
                    ];
                    setAreaOptions(options);
                }
            } catch (error) {
                console.error('加载地区数据失败:', error);
            }
        };

        if (showArea) {
            loadAreaData();
        }
    }, [showArea]);

    // 加载公司列表
    useEffect(() => {
        const loadOrganizations = async () => {
            try {
                const response = await getOrganizationList({
                    pageIndex: 1,
                    pageSize: 100
                });
                if (response && response.ok) {
                    const options = [
                        { label: '全部', value: '' },
                        ...(response.data.Items || []).map(org => ({
                            label: org,
                            value: org
                        }))
                    ];
                    setOrganizationOptions(options);
                }
            } catch (error) {
                console.error('加载公司列表失败:', error);
            }
        };

        if (showOrganization) {
            loadOrganizations();
        }
    }, [showOrganization]);

    // 触发筛选条件变化
    useEffect(() => {
        if (onFilterChange) {
            const filterData = {
                plateTypes: selectedCategory[0] && selectedCategory[0] !== '全部' ? [selectedCategory[0]] : [],
                provinces: selectedArea.length > 0 && selectedArea[0] !== '全部' ? selectedArea : [],
                organizations: selectedOrganization[0] && selectedOrganization[0] !== '' ? [selectedOrganization[0]] : [],
                sort: selectedSort[0] || ''
            };
            onFilterChange(filterData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategory, selectedArea, selectedOrganization, selectedSort]);

    if (!visible) {
        return null;
    }

    return (
        <div className="filter-bar">
            <div className="filter-row">
                {showCategory && (
                    <Picker
                        columns={[categoryOptions]}
                        visible={categoryPickerVisible}
                        onClose={() => {
                            setCategoryPickerVisible(false);
                        }}
                        value={selectedCategory}
                        onConfirm={(value) => {
                            setSelectedCategory(value);
                            setCategoryPickerVisible(false);
                        }}
                    >
                        {(items) => {
                            return (
                                <div
                                    className="filter-item"
                                    onClick={() => {
                                        setCategoryPickerVisible(true);
                                    }}
                                >
                                    <span className="filter-label">
                                        {items[0]?.label || '类别'}
                                    </span>
                                    <svg className="filter-arrow" viewBox="0 0 24 24" style={{ width: 16, height: 16 }}>
                                        <path d="M7 10l5 5 5-5z" fill="currentColor" />
                                    </svg>
                                </div>
                            );
                        }}
                    </Picker>
                )}

                {showArea && (
                    <CascadePicker
                        options={areaOptions}
                        visible={areaPickerVisible}
                        onClose={() => {
                            setAreaPickerVisible(false);
                        }}
                        value={selectedArea}
                        onConfirm={(value) => {
                            setSelectedArea(value);
                            setAreaPickerVisible(false);
                        }}
                    >
                        {(items) => {
                            const displayText = items && items.length > 0
                                ? items.filter(item => item).map(item => item.label).join('-')
                                : '地区';

                            return (
                                <div
                                    className="filter-item"
                                    onClick={() => {
                                        setAreaPickerVisible(true);
                                    }}
                                >
                                    <span className="filter-label">
                                        {displayText}
                                    </span>
                                    <svg className="filter-arrow" viewBox="0 0 24 24" style={{ width: 16, height: 16 }}>
                                        <path d="M7 10l5 5 5-5z" fill="currentColor" />
                                    </svg>
                                </div>
                            );
                        }}
                    </CascadePicker>
                )}

                {showOrganization && (
                    <Picker
                        columns={[organizationOptions]}
                        visible={organizationPickerVisible}
                        onClose={() => {
                            setOrganizationPickerVisible(false);
                        }}
                        value={selectedOrganization}
                        onConfirm={(value) => {
                            setSelectedOrganization(value);
                            setOrganizationPickerVisible(false);
                        }}
                    >
                        {(items) => {
                            return (
                                <div
                                    className="filter-item"
                                    onClick={() => {
                                        setOrganizationPickerVisible(true);
                                    }}
                                >
                                    <span className="filter-label">
                                        {items[0]?.label || '猪场'}
                                    </span>
                                    <svg className="filter-arrow" viewBox="0 0 24 24" style={{ width: 16, height: 16 }}>
                                        <path d="M7 10l5 5 5-5z" fill="currentColor" />
                                    </svg>
                                </div>
                            );
                        }}
                    </Picker>
                )}

                {showSort && (
                    <Picker
                        columns={[sortOptions]}
                        visible={sortPickerVisible}
                        onClose={() => {
                            setSortPickerVisible(false);
                        }}
                        value={selectedSort}
                        onConfirm={(value) => {
                            setSelectedSort(value);
                            setSortPickerVisible(false);
                        }}
                    >
                        {(items) => {
                            return (
                                <div
                                    className="filter-item"
                                    onClick={() => {
                                        setSortPickerVisible(true);
                                    }}
                                >
                                    <span className="filter-label">
                                        {items[0]?.label || '最新'}
                                    </span>
                                    <svg className="filter-arrow" viewBox="0 0 24 24" style={{ width: 16, height: 16 }}>
                                        <path d="M7 10l5 5 5-5z" fill="currentColor" />
                                    </svg>
                                </div>
                            );
                        }}
                    </Picker>
                )}
            </div>
        </div>
    );
}
